import moment from 'moment-timezone'
import { UserOutlined } from '@ant-design/icons'
import { formatPhoneNumber } from '@Admin/AdminQuoteDetailsPage/logic'

import { useUserPhoneNumberUserProfileQuery } from '~/gql/graphql'
import Box from '~/core/Box'
import Link from '~/core/Link'
import Text from '~/core/Text'
import { IdentityType } from '~/redux-modules/identity/type'
import { HELP_EMAIL } from '~/lib/emailAndUrls'
import { mailtoLink } from '~/lib/mailtoLink'
import { EditLink } from '../components'
import {
    BORDER,
    ICON_STYLE,
    TEXT_H3_STYLE,
    TEXT_LABEL_STYLE,
} from '../styleConstants'
import pageStyles from '../styles.css'

import { NameForm, PhoneNumberForm, DeleteAccountButton } from './components'
import { NAME, PHONE_NUMBER } from './constants'

type PersonalInformationPropsType = {
    identity: IdentityType
    selectedField: string | null
    handleSelectEdit: (selectedField: string | null) => void
    popConfirmation?: string | null
    setCurrentForm: Function
    isBusinessAccountMember?: boolean
}

const PLACEMENT = 'my-account-personal-information'

export default function PersonalInformation(
    props: PersonalInformationPropsType,
) {
    const {
        identity,
        selectedField,
        handleSelectEdit,
        popConfirmation,
        setCurrentForm,
    } = props

    const { data } = useUserPhoneNumberUserProfileQuery()
    const isBusinessAccountMember = Boolean(
        identity.businessAccountMembership?.businessAccount?.isEnabled,
    )

    return (
        <Box className={pageStyles.section}>
            <Box className={pageStyles.header}>
                <UserOutlined style={ICON_STYLE} />
                <Text style={TEXT_H3_STYLE}>Personal Information</Text>
            </Box>
            <Box
                className={pageStyles.mobileColBox}
                padding="24px 0"
                borderBottom={BORDER}
            >
                <Box className={pageStyles.mobileLabel}>
                    <Text style={TEXT_LABEL_STYLE}>NAME</Text>
                </Box>
                {selectedField === NAME ? (
                    <NameForm
                        identity={identity}
                        warning={popConfirmation === NAME}
                        onCancel={() => handleSelectEdit(null)}
                        setCurrentForm={setCurrentForm}
                    />
                ) : (
                    <Box position="relative" flex="1">
                        <Box direction="col">
                            <Text
                                style={{
                                    textTransform: 'capitalize',
                                }}
                            >
                                {identity?.name}
                            </Text>
                            {identity?.createdAt && (
                                <Text color="blackAlpha45">
                                    Account created on{' '}
                                    {moment(identity.createdAt).format(
                                        'MMM DD, YYYY',
                                    )}
                                </Text>
                            )}
                        </Box>
                        <EditLink
                            className={pageStyles.mobileEditLink}
                            field={NAME}
                            handleSelectEdit={handleSelectEdit}
                        />
                    </Box>
                )}
            </Box>
            <Box
                padding="24px 0"
                borderBottom={BORDER}
                className={`${pageStyles.mobileColBox} ${pageStyles.mobileNoBorder}`}
            >
                <Box
                    className={`${pageStyles.mobileLabel} ${pageStyles.mobileColBox}`}
                >
                    <Text style={TEXT_LABEL_STYLE}>PHONE NUMBER</Text>
                </Box>
                {selectedField === PHONE_NUMBER ? (
                    <PhoneNumberForm
                        identity={identity}
                        warning={popConfirmation === PHONE_NUMBER}
                        onCancel={() => handleSelectEdit(null)}
                        setCurrentForm={setCurrentForm}
                        phoneNumber={data?.me?.phoneNumber}
                    />
                ) : (
                    <Box position="relative" flex="1">
                        <Text>
                            {formatPhoneNumber(data?.me?.phoneNumber, true) ||
                                ''}
                        </Text>
                        <EditLink
                            className={pageStyles.mobileEditLink}
                            field={PHONE_NUMBER}
                            handleSelectEdit={handleSelectEdit}
                        />
                    </Box>
                )}
            </Box>
            {!isBusinessAccountMember ? (
                <DeleteAccountButton
                    className={pageStyles.mobileHide}
                    identity={identity}
                />
            ) : (
                <Text style={{ marginTop: '16px', marginBottom: '16px' }}>
                    To deactivate your account or leave your company's Business
                    Account please
                    <Link
                        name="contact-us-deactivate-account-link"
                        data-test-target="contact-us-deactivate-account-link"
                        placement={PLACEMENT}
                        href={mailtoLink(
                            HELP_EMAIL,
                            "I'd like to deactivate my account",
                        )}
                    >
                        {' '}
                        contact us
                    </Link>
                </Text>
            )}
        </Box>
    )
}
